import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts';
import { Pill } from '../../../../components-v2/Pill';
import { Skeleton } from '../../../../components-v2/Skeleton';
import { TabToggle } from '../../../../components-v2/TabToggle';
import { THEME } from '../../../../components-v2/Theme/theme';
import { IMovementRow } from './historyData';
import { PendingHistory } from './PendingHistory';
import { styleSheet } from './styles';

type MovementView = 'chart' | 'table';

const VIEWS = [
	{ value: 'chart', label: 'Chart' },
	{ value: 'table', label: 'Table' },
];

const GAIN_COLOR = THEME.colors.chart.levBlue;
const LOSS_COLOR = THEME.colors.chart.magicOrange;
const ASSUMED_COLOR = THEME.colors.chart.illusionYellow;

const formatMonth = (month: string) => moment(month, 'YYYY-MM').format('MMM YYYY');
const formatCount = (n: number) => n.toLocaleString('en-US');
const formatMovement = (n: number, sign = '') => (n ? `${sign}${formatCount(n)}` : '—');

/** Losses still in the confirmation window are shown with the month's losses; the month is marked preliminary. */
const lossCount = (row: IMovementRow) => row.lost.count + row.pendingLoss.count;

function MovementTooltip({
	active,
	payload,
}: {
	active?: boolean;
	payload?: Array<{
		payload: { month: string; newBusiness: number; lost: number; assumedCancelled: number; preliminary: boolean };
	}>;
}) {
	const point = payload?.[0]?.payload;
	if (!active || !point) {
		return null;
	}
	return (
		<div className={css(styleSheet.tooltip)}>
			<div className={css(styleSheet.tooltipTitle)}>{point.month}</div>
			<p className={css(styleSheet.tooltipLine)}>New business {formatMovement(point.newBusiness, '+')}</p>
			<p className={css(styleSheet.tooltipLine)}>Lost {formatMovement(Math.abs(point.lost), '−')}</p>
			<p className={css(styleSheet.tooltipLine)}>
				Assumed cancelled {formatMovement(Math.abs(point.assumedCancelled), '−')}
			</p>
			{point.preliminary ? (
				<p className={css(styleSheet.tooltipNote)}>
					Preliminary: losses from the last 30 days are still being confirmed, so this month is shown lighter and can
					still change.
				</p>
			) : null}
		</div>
	);
}

export const BookMovement = ({
	rows,
	loading,
	firstMonthOn,
}: {
	rows: IMovementRow[];
	loading?: boolean;
	/** Set while the first full month is still being tracked: the date it will show. */
	firstMonthOn?: string;
}) => {
	const [view, setView] = React.useState<MovementView>('chart');

	const chartData = rows.map(row => ({
		month: moment(row.month, 'YYYY-MM').format("MMM 'YY"),
		newBusiness: row.newBusiness.count,
		lost: -lossCount(row),
		assumedCancelled: -row.assumedCancelled.count,
		preliminary: row.preliminary,
	}));

	const totals = rows.reduce(
		(sum, row) => ({
			newBusiness: sum.newBusiness + row.newBusiness.count,
			renewed: sum.renewed + row.renewed.count,
			lost: sum.lost + lossCount(row),
			assumedCancelled: sum.assumedCancelled + row.assumedCancelled.count,
		}),
		{ newBusiness: 0, renewed: 0, lost: 0, assumedCancelled: 0 }
	);

	return (
		<section className={css(styleSheet.card)}>
			<div className={css(styleSheet.cardHeader)}>
				<div>
					<h3 className={css(styleSheet.cardTitle)}>Book movement</h3>
					<p className={css(styleSheet.cardSubtitle)}>Policies gained and lost each month.</p>
				</div>
				{firstMonthOn ? null : (
					<TabToggle
						items={VIEWS}
						value={view}
						onValueChange={value => setView(value as MovementView)}
						size='small'
						aria-label='Movement view'
					/>
				)}
			</div>

			{firstMonthOn ? (
				<PendingHistory title={`First month shows on ${firstMonthOn}`}>
					New, renewed and lost policies, month by month, once a full month has been tracked.
				</PendingHistory>
			) : loading ? (
				<Skeleton>
					<p className={css(styleSheet.empty)}>Loading book movement...</p>
				</Skeleton>
			) : rows.length === 0 ? (
				<p className={css(styleSheet.empty)}>No movement recorded in this period yet.</p>
			) : view === 'chart' ? (
				<div className={css(styleSheet.movementBody)}>
					<ResponsiveContainer width='100%' height={260}>
						<BarChart data={chartData} stackOffset='sign' margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
							<CartesianGrid strokeDasharray='3 3' stroke={THEME.colors.border.main} vertical={false} />
							<XAxis dataKey='month' tick={{ fontSize: THEME.typography.fontSizes.xs, fill: THEME.colors.text.main }} />
							<YAxis
								allowDecimals={false}
								tickFormatter={(value: number) => formatCount(Math.abs(value))}
								tick={{ fontSize: THEME.typography.fontSizes.xs, fill: THEME.colors.text.main }}
							/>
							<Tooltip content={<MovementTooltip />} cursor={{ fill: THEME.colors.info.light }} />
							<ReferenceLine y={0} stroke={THEME.colors.border.main} />
							<Bar dataKey='newBusiness' name='New business' stackId='movement' fill={GAIN_COLOR}>
								{chartData.map(d => (
									<Cell key={d.month} fillOpacity={d.preliminary ? 0.5 : 1} />
								))}
							</Bar>
							<Bar dataKey='lost' name='Lost' stackId='movement' fill={LOSS_COLOR}>
								{chartData.map(d => (
									<Cell key={d.month} fillOpacity={d.preliminary ? 0.5 : 1} />
								))}
							</Bar>
							<Bar dataKey='assumedCancelled' name='Assumed cancelled' stackId='movement' fill={ASSUMED_COLOR} />
						</BarChart>
					</ResponsiveContainer>
					<div className={css(styleSheet.totals)}>
						<p className={css(styleSheet.totalsHeading)}>This period</p>
						<div className={css(styleSheet.totalRow)}>
							<span className={css(styleSheet.swatch, styleSheet.swatchNewBusiness)} />
							<span className={css(styleSheet.totalLabel)}>New business</span>
							<span className={css(styleSheet.totalValue)}>{formatMovement(totals.newBusiness, '+')}</span>
						</div>
						<div className={css(styleSheet.totalRow)}>
							<span className={css(styleSheet.swatch, styleSheet.swatchRenewed)} />
							<span className={css(styleSheet.totalLabel)}>Renewed</span>
							<span className={css(styleSheet.totalValue)}>{formatMovement(totals.renewed)}</span>
						</div>
						<div className={css(styleSheet.totalRow)}>
							<span className={css(styleSheet.swatch, styleSheet.swatchLost)} />
							<span className={css(styleSheet.totalLabel)}>Lost</span>
							<span className={css(styleSheet.totalValue)}>{formatMovement(totals.lost, '−')}</span>
						</div>
						<div className={css(styleSheet.totalRow)}>
							<span className={css(styleSheet.swatch, styleSheet.swatchAssumedCancelled)} />
							<span className={css(styleSheet.totalLabel)}>Assumed cancelled</span>
							<span className={css(styleSheet.totalValue)}>{formatMovement(totals.assumedCancelled, '−')}</span>
						</div>
					</div>
				</div>
			) : (
				<div className={css(styleSheet.tableScroll)}>
					<table className={css(styleSheet.table)}>
						<thead>
							<tr>
								<th className={css(styleSheet.th, styleSheet.firstColumn)}>Month</th>
								<th className={css(styleSheet.th)}>New</th>
								<th className={css(styleSheet.th)}>Renewed</th>
								<th className={css(styleSheet.th)}>Lost</th>
								<th className={css(styleSheet.th)}>Assumed cancelled</th>
							</tr>
						</thead>
						<tbody>
							{[...rows].reverse().map(row => (
								<tr key={row.month}>
									<td className={css(styleSheet.td, styleSheet.firstColumn)}>
										<span className={css(styleSheet.monthCell)}>
											{formatMonth(row.month)}
											{row.preliminary ? <Pill variant='label' color='yellow' label='Preliminary' /> : null}
										</span>
									</td>
									<td className={css(styleSheet.td)}>{formatMovement(row.newBusiness.count, '+')}</td>
									<td className={css(styleSheet.td)}>{formatMovement(row.renewed.count)}</td>
									<td className={css(styleSheet.td)}>{formatMovement(lossCount(row), '−')}</td>
									<td className={css(styleSheet.td)}>{formatMovement(row.assumedCancelled.count, '−')}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{firstMonthOn ? null : (
				<p className={css(styleSheet.note)}>
					Assumed cancelled: dropped out of a CSV upload, which gives no reason. Losses count once they’ve held for 30
					days, so preliminary months can still change.
				</p>
			)}
		</section>
	);
};
